import { useState, useCallback } from 'react';
import { useAuth0 } from 'Auth';
import { postBlockListAttributesBatch } from 'Shared/services/Reznor/PostBlockListAttributesBatch';
import type { BatchCreateBlockedAttributePayload } from 'Shared/services/Reznor/PostBlockListAttributesBatch';
import { extractReznorError } from 'Shared/services/Reznor/extractReznorError';
import type { BlockListRow } from 'BlockList/types';

type Callbacks = {
  onSuccess: (created: BlockListRow[]) => void;
  onError: (message: string) => void;
};

export type UseBatchBlockAttributesResult = {
  submitting: boolean;
  submitError: string | null;
  submit: (
    payload: BatchCreateBlockedAttributePayload,
    callbacks: Callbacks
  ) => Promise<void>;
};

export const useBatchBlockAttributes = (): UseBatchBlockAttributesResult => {
  const { getToken } = useAuth0();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = useCallback(
    async (
      payload: BatchCreateBlockedAttributePayload,
      callbacks: Callbacks
    ) => {
      setSubmitting(true);
      setSubmitError(null);

      try {
        const token = await getToken();
        const response = await postBlockListAttributesBatch(token, payload);

        if (response && response.status === 201 && response.data) {
          callbacks.onSuccess(response.data);
        } else {
          const message = 'Failed to add blocked attributes.';
          setSubmitError(message);
          callbacks.onError(message);
        }
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { status?: number; data?: unknown };
          message?: string;
        };
        const message =
          extractReznorError(axiosErr.response?.data) ??
          axiosErr.message ??
          'Failed to add blocked attributes.';
        setSubmitError(message);
        callbacks.onError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [getToken]
  );

  return { submitting, submitError, submit };
};
